import React, { useState } from 'react';
import {
  Space,
  Card,
  Table,
  Tag,
  Typography,
  Button,
  Select,
  Row,
  Col,
  Statistic,
  DatePicker,
  Alert,
  Empty,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ReloadOutlined,
  DollarOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useCreditAnalytics } from '../hooks/useCreditAnalytics';
import {
  CreditTimeSeriesPoint,
  CreditAnomaly,
} from '../../api/services/adminService';
import UserCreditSummaryModal from './UserCreditSummaryModal';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const SEVERITY_COLORS: Record<'LOW' | 'MEDIUM' | 'HIGH', string> = {
  LOW: 'gold',
  MEDIUM: 'orange',
  HIGH: 'red',
};

const CreditAnalytics: React.FC = () => {
  const { t } = useTranslation();
  const ANOMALY_LABELS: Record<string, string> = {
    HIGH_DAILY_USAGE: t('admin.credit_analytics.anomaly_high_daily'),
    BUY_BURST: t('admin.credit_analytics.anomaly_buy_burst'),
    LARGE_PURCHASE_OUTLIER: t('admin.credit_analytics.anomaly_large_purchase'),
  };
  const {
    analytics,
    loading,
    dateRange,
    groupBy,
    handleDateRangeChange,
    handleGroupByChange,
    refetch,
  } = useCreditAnalytics();

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [summaryVisible, setSummaryVisible] = useState(false);

  const handleUserClick = (userId: string) => {
    setSelectedUserId(userId);
    setSummaryVisible(true);
  };

  // Time series table columns
  const timeSeriesColumns: ColumnsType<CreditTimeSeriesPoint> = [
    {
      title: t('admin.credit_analytics.col_period'),
      dataIndex: 'period',
      key: 'period',
      render: (v: string) => {
        const d = new Date(v);
        if (groupBy === 'month') return d.toLocaleDateString('en', { year: 'numeric', month: 'long' });
        if (groupBy === 'week') return t('admin.credit_analytics.week_of', { date: d.toLocaleDateString() });
        return d.toLocaleDateString();
      },
    },
    {
      title: t('admin.credit_analytics.col_credits_purchased'),
      dataIndex: 'creditsPurchased',
      key: 'creditsPurchased',
      align: 'right',
      render: (v: number) => <Text style={{ color: '#52c41a' }}>+{v}</Text>,
    },
    {
      title: t('admin.credit_analytics.col_credits_used'),
      dataIndex: 'creditsUsed',
      key: 'creditsUsed',
      align: 'right',
      render: (v: number) => <Text style={{ color: '#ff4d4f' }}>-{v}</Text>,
    },
    {
      title: t('admin.credit_analytics.col_net'),
      key: 'net',
      align: 'right',
      render: (_: unknown, record: CreditTimeSeriesPoint) => {
        const net = record.creditsPurchased - record.creditsUsed;
        return <Text strong style={{ color: net >= 0 ? '#52c41a' : '#ff4d4f' }}>{net >= 0 ? `+${net}` : net}</Text>;
      },
    },
  ];

  // Top buyers table columns
  const topBuyersColumns = [
    {
      title: '#',
      dataIndex: 'rank',
      key: 'rank',
      width: 50,
    },
    {
      title: t('admin.credit_analytics.col_user'),
      key: 'user',
      render: (_: unknown, record: { userId: string; name: string; email: string }) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', color: '#fe5a1d' }}
            onClick={() => handleUserClick(record.userId)}
          >
            {record.name}
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.email}
          </Text>
        </Space>
      ),
    },
    {
      title: t('admin.credit_analytics.col_credits_purchased'),
      dataIndex: 'totalCreditsPurchased',
      key: 'totalCreditsPurchased',
      align: 'right' as const,
      render: (v: number) => <Text strong style={{ color: '#fe5a1d' }}>{v}</Text>,
    },
  ];

  // Anomalies table columns
  const anomalyColumns: ColumnsType<CreditAnomaly> = [
    {
      title: t('admin.credit_analytics.col_user'),
      key: 'user',
      render: (_: unknown, record: CreditAnomaly) => (
        <Space direction="vertical" size={0}>
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', color: '#fe5a1d' }}
            onClick={() => handleUserClick(record.userId)}
          >
            {record.userName}
          </Button>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.userEmail}
          </Text>
        </Space>
      ),
    },
    {
      title: t('admin.credit_analytics.col_anomaly_type'),
      dataIndex: 'anomalyType',
      key: 'anomalyType',
      width: 160,
      render: (type: string, record: CreditAnomaly) => (
        <Tag color={SEVERITY_COLORS[record.severity]}>
          {ANOMALY_LABELS[type] || type}
        </Tag>
      ),
    },
    {
      title: t('admin.credit_analytics.col_severity'),
      dataIndex: 'severity',
      key: 'severity',
      width: 90,
      render: (s: 'LOW' | 'MEDIUM' | 'HIGH') => (
        <Tag color={SEVERITY_COLORS[s]}>{s}</Tag>
      ),
    },
    {
      title: t('admin.credit_analytics.col_details'),
      dataIndex: 'details',
      key: 'details',
      ellipsis: true,
    },
    {
      title: t('admin.credit_analytics.col_detected'),
      dataIndex: 'detectedAt',
      key: 'detectedAt',
      width: 150,
      render: (v: string) => new Date(v).toLocaleString(),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Title level={4} style={{ margin: 0 }}>
            {t('admin.credit_analytics.title')}
          </Title>
          <Text type="secondary">{t('admin.credit_analytics.subtitle')}</Text>
        </div>
        <Space>
          <RangePicker
            className="admin-filter-picker"
            value={
              dateRange ? [dayjs(dateRange[0]), dayjs(dateRange[1])] : null
            }
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                handleDateRangeChange([dates[0].toISOString(), dates[1].toISOString()]);
              } else {
                handleDateRangeChange(null);
              }
            }}
          />
          <Select
            className="admin-filter-select"
            value={groupBy}
            onChange={handleGroupByChange}
            options={[
              { value: 'day', label: t('admin.credit_analytics.group_day') },
              { value: 'week', label: t('admin.credit_analytics.group_week') },
              { value: 'month', label: t('admin.credit_analytics.group_month') },
            ]}
            style={{ width: 110 }}
          />
          <Button type="primary" className="ant-btn-primary admin-filter-btn admin-filter-btn-primary" icon={<ReloadOutlined />} onClick={refetch} loading={loading}>
            {t('admin.credit_analytics.btn_refresh')}
          </Button>
        </Space>
      </div>

      {/* Revenue Card */}
      <Row gutter={16}>
        <Col xs={24} md={8}>
          <Card bordered={false} className="admin-stat-card" style={{ background: '#fff7e6' }}>
            <Statistic
              title={t('admin.credit_analytics.stat_total_revenue')}
              value={analytics?.revenue.total ?? 0}
              prefix={<DollarOutlined />}
              suffix={analytics?.revenue.currency ?? 'ALL'}
              valueStyle={{ color: '#fe5a1d' }}
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t('admin.credit_analytics.stat_completed_txns', { count: analytics?.revenue.transactionCount ?? 0 })}
            </Text>
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} className="admin-stat-card" style={{ background: '#f6ffed' }}>
            <Statistic
              title={t('admin.credit_analytics.stat_credits_purchased')}
              value={analytics?.timeSeries.reduce((s, r) => s + r.creditsPurchased, 0) ?? 0}
              prefix={<ArrowUpOutlined />}
              valueStyle={{ color: '#52c41a' }}
              suffix={t('admin.credit_analytics.suffix_credits')}
            />
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card bordered={false} className="admin-stat-card" style={{ background: '#fff1f0' }}>
            <Statistic
              title={t('admin.credit_analytics.stat_credits_used')}
              value={analytics?.timeSeries.reduce((s, r) => s + r.creditsUsed, 0) ?? 0}
              prefix={<ArrowDownOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
              suffix={t('admin.credit_analytics.suffix_credits')}
            />
          </Card>
        </Col>
      </Row>

      {/* Time Series */}
      <Card
        bordered={false}
        className="admin-table-card"
        title={<Text strong>{t('admin.credit_analytics.section_credits_over_time')}</Text>}
      >
        <Table<CreditTimeSeriesPoint>
          rowKey="period"
          loading={loading}
          dataSource={analytics?.timeSeries ?? []}
          columns={timeSeriesColumns}
          pagination={{ pageSize: 15, showSizeChanger: false }}
          size="small"
          locale={{ emptyText: t('admin.credit_analytics.empty_no_data') }}
        />
      </Card>

      {/* Top Buyers */}
      <Card
        bordered={false}
        className="admin-table-card"
        title={<Text strong>{t('admin.credit_analytics.section_top_buyers')}</Text>}
      >
        <Table
          rowKey="userId"
          loading={loading}
          dataSource={analytics?.topBuyers ?? []}
          columns={topBuyersColumns}
          pagination={false}
          size="small"
          locale={{ emptyText: t('admin.credit_analytics.empty_no_purchase') }}
        />
      </Card>

      {/* Anomalies */}
      <Card
        bordered={false}
        className="admin-table-card"
        title={
          <Space>
            <WarningOutlined style={{ color: '#fa8c16' }} />
            <Text strong>{t('admin.credit_analytics.section_anomalies')}</Text>
            {analytics && analytics.anomalies.length > 0 && (
              <Tag color="red">{t('admin.credit_analytics.anomalies_detected', { count: analytics.anomalies.length })}</Tag>
            )}
          </Space>
        }
      >
        {analytics && analytics.anomalies.length === 0 ? (
          <Alert
            message={t('admin.credit_analytics.no_anomalies_title')}
            description={t('admin.credit_analytics.no_anomalies_desc')}
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
          />
        ) : analytics && analytics.anomalies.length > 0 ? (
          <Table<CreditAnomaly>
            rowKey={(r) => `${r.userId}-${r.anomalyType}-${r.detectedAt}`}
            loading={loading}
            dataSource={analytics.anomalies}
            columns={anomalyColumns}
            pagination={false}
            size="small"
            scroll={{ x: 700 }}
          />
        ) : (
          <Empty description={t('admin.credit_analytics.loading')} />
        )}
      </Card>

      {/* User Credit Summary Modal */}
      <UserCreditSummaryModal
        userId={selectedUserId}
        visible={summaryVisible}
        onClose={() => {
          setSummaryVisible(false);
          setSelectedUserId(null);
        }}
      />
    </Space>
  );
};

export default CreditAnalytics;
